import * as Clipboard from 'expo-clipboard';
import { openURL } from 'expo-linking';
import linkifyIt from 'linkify-it';
import React from 'react';
import { Platform } from 'react-native';
import tlds from 'tlds';

import { Alert } from '@components/Alert';
import { handleTaskTagUrlInApp } from '@utils/internalLinks';

const linkify = new linkifyIt();
export const linkifyConfig = linkify.tlds(tlds);

// linkify-it ships no type declarations, so its `.match()` result is
// implicitly `any` -- this is the shape actually used here.
type LinkifyMatch = {
  index: number;
  lastIndex: number;
  text: string;
  url: string;
};

/**
 * Opens a matched URL. Keeps TaskTag invite/app links inside the app (iOS
 * would otherwise hand them to Safari); every other URL falls through to
 * the OS/browser handler.
 */
export const openLinkUrl = (url: string): void => {
  if (handleTaskTagUrlInApp(url)) return;
  if (Platform.OS === 'web') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    openURL(url).catch(() => Alert.alert(`invalid URL: ${url}`));
  }
};

/** Copies a matched URL to the clipboard, then reports it via `onCopied`. */
export const copyLinkUrl = (
  url: string,
  onCopied: (message: string) => void
): void => {
  Clipboard.setStringAsync(url).then(() => {
    onCopied(`${url} copied to clipboard`);
  });
};

/**
 * Splits `text` into plain-text and link segments via the caller-supplied
 * renderers, or returns `null` when `text` has no links (so the caller can
 * render it unchanged).
 *
 * Produces only sibling nodes -- never introduces a wrapping `View` -- so
 * the result can be safely interleaved inside any outer `<Text>` tree
 * without breaking native text layout. `react-native-hyperlink` always
 * rendered a `View` wrapper around its children; placed alongside other
 * inline Text content (e.g. @mentions) inside an outer `<Text>`, React
 * Native's inline text layout mismeasured that View child, producing
 * overlapping/broken text on native (TA-10489). This also removed a
 * standing `patch-package` patch needed just to keep the library from
 * crashing on React 19 (TA-10066) and a Babel/webpack transpile-path
 * workaround needed to ship it into the web bundle at all (TA-10032) --
 * replacing the dependency closes all three failure classes at once
 * instead of working around each individually (TA-10697).
 */
export const splitTextWithLinks = (
  text: string,
  keyPrefix: string,
  renderPlain: (segment: string, key: string) => React.ReactNode,
  renderLink: (url: string, displayText: string, key: string) => React.ReactNode
): React.ReactNode[] | null => {
  if (!linkifyConfig.pretest(text) || !linkifyConfig.test(text)) return null;

  const matches = linkifyConfig.match(text);
  if (!matches || matches.length === 0) return null;

  const segments: React.ReactNode[] = [];
  let cursor = 0;

  (matches as LinkifyMatch[]).forEach((match, matchIndex) => {
    const {
      index: matchStart,
      lastIndex: matchEnd,
      text: linkDisplayText,
      url,
    } = match;

    if (matchStart > cursor) {
      segments.push(
        renderPlain(
          text.slice(cursor, matchStart),
          `${keyPrefix}-pre-${matchIndex}`
        )
      );
    }

    segments.push(
      renderLink(url, linkDisplayText, `${keyPrefix}-url-${matchIndex}`)
    );
    cursor = matchEnd;
  });

  if (cursor < text.length) {
    segments.push(renderPlain(text.slice(cursor), `${keyPrefix}-post`));
  }

  return segments;
};
